import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleOf } from '@il-colpo/shared/account';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { useAccount } from '../../account/accountStore.ts';
import { useFlag } from '../../meta/liveStore.ts';
import { sendInvite } from '../../social/gameServerApi.ts';
import { useSocialStore } from '../../social/socialStore.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { Button, Panel } from '../../ui/index.ts';
import styles from './Social.module.css';

/** In a private room that has not started: the friends who are here, each with a button to invite them. */
export function RoomInvites() {
  const { t } = useTranslation('social');
  const enabled = useFlag('friends');
  const guest = useAccount((s) => s.snapshot.user?.anonymous ?? true);
  const overview = useSocialStore((s) => s.overview);
  const presence = useSocialStore((s) => s.presence);
  const push = useToastStore((s) => s.push);
  const [pending, setPending] = useState<string | null>(null);
  if (!enabled || guest || !overview) return null;
  // Only a friend who is here and not at another table can be invited.
  const here = overview.friends.filter((f) => presence[f.userId]?.state === 'online');

  const invite = async (userId: string, name: string) => {
    setPending(userId);
    const answer = await sendInvite(userId);
    setPending(null);
    if (!answer) {
      push(t('room.error.unreachable'), 'danger');
    } else if (answer.ok) {
      push(t('room.sent', { name }), 'success');
    } else {
      push(
        t(
          `room.error.${answer.error === 'unauthorized' || answer.error === 'badRequest' ? 'generic' : answer.error}`,
          { name },
        ),
        'info',
      );
    }
  };

  return (
    <Panel title={t('room.title')}>
      {here.length === 0 ? (
        <p className={styles['note']}>{t('room.empty')}</p>
      ) : (
        <ul className={styles['list']}>
          {here.map((f) => {
            const name = handleOf(f.nickname, f.tag);
            return (
              <li key={f.userId} className={styles['person']} data-testid="room-friend">
                <AvatarFigure avatar={f.avatar} size="2.6rem" />
                <span className={styles['personName']}>
                  <strong>{name}</strong>
                </span>
                <span className={styles['actions']}>
                  <Button
                    loading={pending === f.userId}
                    onClick={() => {
                      void invite(f.userId, name);
                    }}
                  >
                    {t('room.action')}
                  </Button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
